import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { FaBell, FaCheck } from "react-icons/fa";

import { callWaiter } from "../actions/orderActions";
import { WAITER_CALL_RESET } from "../constants/orderConstants";
import { t } from "../i18n/translations";
import "./CallWaiterButton.css";

// How long the button stays disabled/confirmed after a successful call
// before a guest can call again. There's no guest-facing endpoint to poll
// the call's real status (only staff can list/patch calls), and adding one
// just to re-enable this button isn't worth the extra public surface for a
// first version — the actual spam guard already lives server-side
// (createWaiterCall reuses any still pending/acknowledged call instead of
// creating a duplicate), so this cooldown is UX only, not the real guard.
const COOLDOWN_MS = 3 * 60 * 1000;

// The button floats right next to other tap targets (add-to-cart "+",
// the cart fab) — a bare single tap fires too easily by accident. Arming
// first and requiring a second tap within this window before the call
// actually goes out cuts down on stray taps without adding a modal.
const ARM_TIMEOUT_MS = 4000;

function CallWaiterButton() {
  const dispatch = useDispatch();
  const location = useLocation();

  const { language } = useSelector((state) => state.language);
  const { restaurant } = useSelector((state) => state.restaurantDetails || {});
  const { loading, success, error } = useSelector((state) => state.waiterCall || {});

  const [armed, setArmed] = useState(false);
  const [confirmedUntil, setConfirmedUntil] = useState(null);
  const armTimeoutRef = useRef(null);
  const cooldownTimeoutRef = useRef(null);

  const table = new URLSearchParams(location.search).get("table");

  useEffect(() => {
    if (!success) return;

    setArmed(false);
    setConfirmedUntil(Date.now() + COOLDOWN_MS);
    dispatch({ type: WAITER_CALL_RESET });

    clearTimeout(cooldownTimeoutRef.current);
    cooldownTimeoutRef.current = setTimeout(
      () => setConfirmedUntil(null),
      COOLDOWN_MS,
    );

    return () => clearTimeout(cooldownTimeoutRef.current);
  }, [success, dispatch]);

  useEffect(
    () => () => {
      clearTimeout(armTimeoutRef.current);
      clearTimeout(cooldownTimeoutRef.current);
    },
    [],
  );

  // Basic+ feature — mirrors the exact tier-check shape RestaurantHero.js
  // already uses for the PRO-only reservations link (restaurant.subscription_tier
  // straight off the public serializer, no separate active-status check
  // needed since inactive restaurants are already excluded from the public
  // menu entirely). Enforced server-side too (createWaiterCall), this is
  // just so lite-tier guests never see a button that would 403.
  const canUseWaiterCalls = ["basic", "pro"].includes(restaurant?.subscription_tier);

  if (!restaurant || !canUseWaiterCalls || restaurant.tables_enabled === false || !table) {
    return null;
  }

  const isConfirmed = confirmedUntil != null;

  const handleClick = () => {
    if (loading || isConfirmed) return;

    if (!armed) {
      setArmed(true);
      clearTimeout(armTimeoutRef.current);
      armTimeoutRef.current = setTimeout(() => setArmed(false), ARM_TIMEOUT_MS);
      return;
    }

    clearTimeout(armTimeoutRef.current);
    setArmed(false);
    dispatch(callWaiter(table));
  };

  return (
    <button
      type="button"
      className={`call-waiter-btn${isConfirmed ? " confirmed" : ""}${armed ? " armed" : ""}`}
      onClick={handleClick}
      disabled={loading || isConfirmed}
      aria-live="polite"
    >
      {isConfirmed ? <FaCheck /> : <FaBell />}
      <span className="call-waiter-btn-text">
        {isConfirmed
          ? t("callWaiterConfirmed", language)
          : loading
            ? t("callWaiterCalling", language)
            : armed
              ? t("callWaiterConfirmPrompt", language)
              : t("callWaiterButton", language)}
      </span>
      {error && !loading && !isConfirmed && (
        <span className="call-waiter-btn-error">{t("callWaiterError", language)}</span>
      )}
    </button>
  );
}

export default CallWaiterButton;
